import { Text, View } from 'react-native'
import { colors } from '@/src/theme'
import { useTranslation } from 'react-i18next'

const NON_SCALING_TEXT_PROPS = {
  allowFontScaling: false,
  maxFontSizeMultiplier: 1,
} as const

type BetaBadgeSize = 'small' | 'large'

const SMALL_BADGE_STYLE = {
  height: 18,
  horizontalPadding: 8,
  fontSize: 10,
  lineHeight: 12,
  letterSpacing: 0.2,
} as const

const LARGE_BADGE_BORDER_RADIUS = 8

const LARGE_BADGE_TITLE_STYLE = {
  color: colors.warningForeground,
  fontFamily: 'OpenSans_700Bold',
  fontSize: 14,
  includeFontPadding: false,
  lineHeight: 18,
  letterSpacing: 0,
} as const

const LARGE_BADGE_BODY_STYLE = {
  color: colors.warningForeground,
  fontFamily: 'OpenSans_400Regular',
  fontSize: 13,
  includeFontPadding: false,
  lineHeight: 18,
  letterSpacing: 0,
} as const

const SMALL_BADGE_TEXT_STYLE = {
  color: colors.warningForeground,
  fontFamily: 'OpenSans_700Bold',
  fontSize: SMALL_BADGE_STYLE.fontSize,
  includeFontPadding: false,
  lineHeight: SMALL_BADGE_STYLE.lineHeight,
  letterSpacing: SMALL_BADGE_STYLE.letterSpacing,
} as const

const SMALL_BADGE_CONTAINER_STYLE = {
  alignItems: 'center',
  backgroundColor: colors.warning,
  height: SMALL_BADGE_STYLE.height,
  justifyContent: 'center',
  paddingHorizontal: SMALL_BADGE_STYLE.horizontalPadding,
} as const

const LARGE_BADGE_CONTAINER_STYLE = {
  alignSelf: 'stretch',
  backgroundColor: colors.warning,
  borderRadius: LARGE_BADGE_BORDER_RADIUS,
  gap: 6,
  paddingHorizontal: 16,
  paddingVertical: 12,
} as const

const BADGE_TEST_IDS = {
  small: {
    title: 'beta-badge-label',
  },
  large: {
    title: 'beta-badge-title',
    body: 'beta-badge-body',
  },
} as const

interface BetaBadgeProps {
  size?: BetaBadgeSize
  testID?: string
}

export function BetaBadge({
  size = 'small',
  testID = 'beta-badge',
}: BetaBadgeProps) {
  const { t } = useTranslation(['common'])
  const label = t('common:beta_badge_label')
  const description = t('common:beta_badge_accessibility_label')

  if (size === 'large') {
    return (
      <View
        accessibilityLabel={description}
        accessibilityRole="text"
        style={LARGE_BADGE_CONTAINER_STYLE}
        testID={testID}
      >
        <Text
          {...NON_SCALING_TEXT_PROPS}
          style={LARGE_BADGE_TITLE_STYLE}
          testID={BADGE_TEST_IDS.large.title}
        >
          {label}
        </Text>
        <Text
          {...NON_SCALING_TEXT_PROPS}
          style={LARGE_BADGE_BODY_STYLE}
          testID={BADGE_TEST_IDS.large.body}
        >
          {description}
        </Text>
      </View>
    )
  }

  return (
    <View
      accessibilityLabel={description}
      accessibilityRole="text"
      className="self-center rounded-full"
      style={SMALL_BADGE_CONTAINER_STYLE}
      testID={testID}
    >
      <Text
        {...NON_SCALING_TEXT_PROPS}
        className="uppercase"
        style={SMALL_BADGE_TEXT_STYLE}
        testID={BADGE_TEST_IDS.small.title}
      >
        {label}
      </Text>
    </View>
  )
}
